import { format, isToday, isYesterday } from 'date-fns'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import LedgerScannerClient, {
  UserActivity,
  UserActivityFilter,
  UserActivityToken,
} from '../clients/LedgerScannerClient'
import { PlugToken } from '../interfaces/common'
import { SwapProvider } from '../interfaces/swapInterface'
import {
  KNOWN_ACTIVITY_KINDS,
  PlugUserActivity,
  PlugUserActivityKind,
} from '../interfaces/transactionActivity'
import { logger } from '../logger'
import { Wallet } from '../providers/WalletProvider'
import { getWalletAddress } from '../utils/getWalletAddress'
import { groupBy } from '../utils/miscHelper'
import useConfig from './useConfig'
import useWallet from './useWallet'

const findToken = (token: UserActivityToken, tokens: PlugToken[]) =>
  tokens.find(
    (t) =>
      t.id === token.id ||
      (t.address?.toLowerCase() === token.address.toLowerCase() && t.chain === token.chain)
  )

const plugifyUserActivity = (
  activity: UserActivity,
  tokens: PlugToken[],
  wallet: Wallet
): PlugUserActivity | undefined => {
  const sentToken = findToken(activity.send_token, tokens)

  if (!sentToken || activity.state === 'failed') return

  const walletAddress = getWalletAddress(sentToken, wallet)
  const isOutgoing =
    walletAddress?.toLowerCase() === activity.from_addr?.toLowerCase() ||
    activity.from_addr?.toLowerCase() === wallet.accountId.toLowerCase()

  const kind = activity.kind as PlugUserActivityKind
  if (!KNOWN_ACTIVITY_KINDS.includes(kind)) return

  if (kind === 'swap' || kind === 'bridge') {
    const receivedToken = findToken(activity.receive_token!, tokens)
    if (!receivedToken) return

    return {
      state: activity.state,
      provider: activity.provider as SwapProvider,
      link: activity.link,
      id: activity.tx_id,
      sent: {
        token: sentToken,
        amount: BigInt(activity.send_token.amount),
        amountUsd: activity.send_token.amount_usd,
        transactionId: activity.send_token.tx_id,
      },
      received: {
        token: receivedToken,
        amount: BigInt(activity.receive_token!.amount),
        amountUsd: activity.receive_token!.amount_usd,
        transactionId: activity.receive_token!.tx_id,
      },
      timestamp: new Date(activity.ts),
      isSwap: true,
      isOutgoing,
      kind,
    }
  } else {
    const base = {
      isOutgoing,
      state: activity.state,
      provider: activity.provider,
      link: activity.link,
      id: activity.tx_id,
      timestamp: new Date(activity.ts),
      isSwap: false as const,
      sent: {
        token: sentToken,
        amount: BigInt(activity.send_token.amount),
        transactionId: activity.send_token.tx_id!,
      },
    }

    if (kind === 'Transfer' || kind === 'Approve') {
      return { ...base, kind, from: activity.from_addr!, to: activity.to_addr! }
    } else if (kind === 'Burn' || kind === 'deposit') {
      return { ...base, kind, from: activity.from_addr! }
    } else if (kind === 'Mint' || kind === 'withdraw') {
      return { ...base, kind, to: activity.to_addr! }
    }
  }
}

const normalizeActivities = (activity: UserActivity[], tokens: PlugToken[], wallet: Wallet) => {
  return activity.map((a) => plugifyUserActivity(a, tokens, wallet)).filter((a) => !!a)
}

export const useUserActivities = (filter?: UserActivityFilter, pageSize?: number) => {
  const { wallet, isWalletReady } = useWallet()
  const { tokens } = useConfig()
  const { t } = useTranslation()

  const [userActivity, setUserActivity] = useState<UserActivity[]>()

  const fetchInfo = useRef({ page: 1, isFetching: false, hasReachedEnd: false })

  const fetchActivity = async (page: number) => {
    fetchInfo.current = { page, isFetching: true, hasReachedEnd: false }

    const activity = await LedgerScannerClient.fetchUserActivity(
      wallet,
      fetchInfo.current.page,
      pageSize,
      filter
    )

    fetchInfo.current = { page, isFetching: false, hasReachedEnd: activity.length === 0 }

    return activity
  }

  const getActivity = async () => {
    if (!isWalletReady) return

    try {
      await fetchActivity(1).then(setUserActivity)
      await LedgerScannerClient.refreshTransactions(wallet).catch(console.error)
      await fetchActivity(1).then(setUserActivity)
    } catch (e) {
      logger.error('Failed to fetch user activity', { originalError: e })
    }
  }

  const reload = async () => {
    setUserActivity([])
    fetchInfo.current = { page: 1, isFetching: false, hasReachedEnd: false }
    await getActivity()
  }

  const fetchNextPage = async () => {
    if (fetchInfo.current.isFetching || fetchInfo.current.hasReachedEnd) return

    const activity = await fetchActivity(fetchInfo.current.page + 1)
    setUserActivity([...(userActivity || []), ...activity])
  }

  useEffect(() => {
    getActivity()
  }, [
    wallet.principalId,
    wallet.solAddress,
    wallet.btcAddress,
    wallet.ethAddress,
    isWalletReady,
    pageSize,
    JSON.stringify(filter),
  ])

  const normalizedActivity = useMemo(() => {
    if (userActivity) {
      return normalizeActivities(userActivity, tokens, wallet)
    }
  }, [userActivity, tokens, wallet])

  const groupUserActivityByDate = useCallback(() => {
    if (!normalizedActivity) return []

    return Object.entries(
      groupBy(normalizedActivity, (activity) => {
        if (isToday(activity.timestamp)) {
          return t('common.today')
        } else if (isYesterday(activity.timestamp)) {
          return t('common.yesterday')
        } else {
          return format(activity.timestamp, 'dd MMMM yyyy')
        }
      })
    )
  }, [normalizedActivity])

  return { userActivity: normalizedActivity, groupUserActivityByDate, fetchNextPage, reload }
}
